import { Injectable } from "@angular/core";
import { CustomerFormService } from "./customer-form.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BusinessCustomerWithCustomerDetails, BuyingOfferSettings, Customer, OverviewUserBuyingOffer } from "@frontend/models";

@Injectable({ providedIn: 'root' })
export class BuyingOfferFormService {

  constructor(
    private readonly _customerFormService: CustomerFormService,
    private readonly _fb: FormBuilder
  ) { }

  createBuyingOfferFormGroup(
    businessCustomer: BusinessCustomerWithCustomerDetails | null,
    customers: Customer[],
    buyingOfferSettings: BuyingOfferSettings,
    overviewUserBuyingOffer?: OverviewUserBuyingOffer,
    defaultPrice?: number,
  ): FormGroup {
    const isBusinessCustomer = !!businessCustomer;
    const userDocuments = overviewUserBuyingOffer?.uploadedDocuments ?? []

    const customersFormArray = this._fb.array(customers.map(c =>
      this._customerFormService.convertToFormGroup(c)
    ));

    const mustHaveUploads = buyingOfferSettings.mustHaveUploadsIndividual ?? [];

    let businessCustomerFormGroup = this._fb.group({});
    if (isBusinessCustomer) {
      businessCustomerFormGroup = this._customerFormService.createBusinessCustomerFormGroup();

      businessCustomerFormGroup.patchValue(businessCustomer);
    }

    const documentsFormArray = this._fb.array(mustHaveUploads.map((name) => {
      return this._fb.group({
        name: name.title,
        id: name.title,
        originalFile: null,
        storageUrl: userDocuments.find((doc) => doc.name === name.title)?.url ?? '',
        loaded: 0,
        uploadSize: 0,
        uploadStatus: '',
        uploadProgress: 0,
      });
    }));

    const otherUserDocs = userDocuments.filter((doc) => !mustHaveUploads.map((m) => m.title).includes(doc.name ?? ''));

    const optionalDocumentsFormArray = this._fb.array(otherUserDocs.map((doc) => {
      return this._fb.group({
        name: doc.name,
        id: doc.name,
        originalFile: null,
        storageUrl: doc.url ?? '',
        loaded: 0,
        uploadSize: 0,
        uploadStatus: '',
        uploadProgress: 0,
      });
    }));

    const timeLimitationDefaultDate = new Date();
    timeLimitationDefaultDate.setDate(timeLimitationDefaultDate.getDate() + 7);

    const detailsFormGroup = this._fb.group({
      price: [overviewUserBuyingOffer?.price ?? null, Validators.required],
      handoverConditionDate: [overviewUserBuyingOffer?.handoverConditionDate],
      handoverConditionNotes: [overviewUserBuyingOffer?.handoverConditionNotes ?? ''],
      timeLimitation: [overviewUserBuyingOffer?.timeLimitation ?? timeLimitationDefaultDate, Validators.required],
      alternativeLimitation: [overviewUserBuyingOffer?.alternativeLimitation ?? ''],
      buyerRestriction: this._fb.group({
        noLimitation: [overviewUserBuyingOffer?.buyerRestriction?.noLimitation ?? true],
        limitationNotes: [overviewUserBuyingOffer?.buyerRestriction?.limitationNotes ?? ''],
      }),
      hasSufficientFinance: [overviewUserBuyingOffer?.hasSufficientFinance ?? null],
    });

    return this._fb.group({
      customers: customersFormArray,
      businessCustomer: businessCustomerFormGroup,
      details: detailsFormGroup,
      documents: documentsFormArray,
      optionalDocuments: optionalDocumentsFormArray,
    });
  }

  createOwnerBuyingOfferFormGroup(
    businessCustomer: BusinessCustomerWithCustomerDetails | null,
    customers: Customer[],
  ): FormGroup {
    const isBusinessCustomer = !!businessCustomer;

    const customersFormArray = this._fb.array<FormGroup<any>>([]);

    let businessCustomerFormGroup = this._fb.group({});
    if (isBusinessCustomer) {
      businessCustomerFormGroup = this._customerFormService.createBusinessCustomerFormGroup();

      businessCustomerFormGroup.patchValue(businessCustomer);
    }

    customers.forEach((c) => {
      customersFormArray.push(this._customerFormService.convertToFormGroup(c, false));
    });

    return this._fb.group({
      businessCustomer: businessCustomerFormGroup,
      customers: customersFormArray,
    });
  }

  public createSellersAcceptanceForm(): FormGroup {

    const timeLimitationDefaultDate = new Date();
    timeLimitationDefaultDate.setDate(timeLimitationDefaultDate.getDate() + 7);


    return this._fb.group({
      sellerAcceptance: this._fb.control('', [Validators.required]),
      changeAcceptanceUntil: [timeLimitationDefaultDate, Validators.required],
      changeDetails: ''
    })
  }

  public createBuyersAcceptanceFormGroup(): FormGroup {
    return this._fb.group({
      buyersAcceptanceWithChange: null,
    })
  }

}